import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton, GlassBadge } from '../../components/ui/GlassComponents';
import { countWorkingDays } from '../../utils/leaveDayCount';

// Calendar components
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

// Add custom CSS for calendar events
import './Calendar.css';

const Calendar = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [bankHolidayEvents, setBankHolidayEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [stats, setStats] = useState({});
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);
  const [workingDays, setWorkingDays] = useState(null); // util defaults to Mon-Fri
  const [leaveBalances, setLeaveBalances] = useState({});
  const [filters, setFilters] = useState({
    showApproved: true,
    showPending: true,
    showRejected: false,
    showCancelled: false,
    showBankHolidays: true,
    leaveTypes: [] // Will be populated from team settings
  });
  
  // Track if user is admin to show/hide certain filters
  const isAdmin = user?.role === 'team_admin' || user?.role === 'super_admin';
  
  // State for date selection and request modal
  const [selectedDates, setSelectedDates] = useState({
    start: null,
    end: null
  });
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [leaveRequest, setLeaveRequest] = useState({
    type: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const calendarRef = useRef(null);
  
  // Track if the component is mounted
  const isMounted = useRef(true);
  
  useEffect(() => {
    // Fetch data when component mounts
    fetchCalendarData();
    
    // Set up event listener for when the page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted.current) {
        fetchCalendarData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Clean up
    return () => {
      isMounted.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      // Fetch all calendar data without date restrictions
      
      const [statsResponse, recentResponse, calendarResponse, leaveTypesResponse, balancesResponse, bankHolidaysResponse, teamResponse] = await Promise.all([
        axios.get('/api/leaves/stats'),
        axios.get('/api/leaves/recent'),
        axios.get('/api/leaves/calendar'), // No date parameters to get all events
        axios.get('/api/leaves/types'),
        axios.get('/api/leaves/balances'),
        axios.get('/api/bank-holidays'),
        axios.get('/api/teams/current').catch(() => null) // soft; util defaults to Mon-Fri
      ]);

      // Set leave balances
      setLeaveBalances(balancesResponse.data || {});

      // Pick up team workingDays so duration math matches the server.
      if (teamResponse && teamResponse.data && Array.isArray(teamResponse.data.workingDays)) {
        setWorkingDays(teamResponse.data.workingDays);
      }
      

      
      // Set available leave types
      const leaveTypes = leaveTypesResponse.data || [];
      setAvailableLeaveTypes(leaveTypes);
      
      // Update filters with available leave types
      // For custom leave types, we use their ID, for standard types we use the type name
      setFilters(prev => ({
        ...prev,
        leaveTypes: leaveTypes.map(type => type.id)
      }));
      
      // Set default leave request type if available
      if (leaveTypes.length > 0 && !leaveRequest.type) {
        setLeaveRequest(prev => ({
          ...prev,
          type: leaveTypes[0].id
        }));
      }
      

      
      setStats(statsResponse.data);
      setRecentLeaves(recentResponse.data);

      // Transform bank holidays to calendar events (read-only)
      const bankHolidays = bankHolidaysResponse?.data?.success ? bankHolidaysResponse.data.events : [];
      const transformedBankHolidays = (bankHolidays || []).map((bh) => {
        const startDate = new Date(bh.date);
        const endDate = new Date(bh.date);
        endDate.setDate(endDate.getDate() + 1);

        return {
          id: `bank-holiday-${bh.date}-${bh.title}`,
          title: `Bank Holiday - ${bh.title}`,
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          allDay: true,
          backgroundColor: '#2563EB',
          borderColor: '#2563EB',
          textColor: '#ffffff',
          classNames: ['bank-holiday-event'],
          extendedProps: {
            isBankHoliday: true,
            bankHoliday: bh
          }
        };
      });

      setBankHolidayEvents(transformedBankHolidays);
      
      if (!Array.isArray(calendarResponse.data)) {
        console.error('Calendar data is not an array:', calendarResponse.data);
        setEvents([]);
        return;
      }
      
      if (calendarResponse.data.length === 0) {
        setEvents([]);
        return;
      }
      
      // Transform leave data to calendar events
      const calendarEvents = calendarResponse.data.map(leave => {
        // Find the leave type in available types to ensure we have color info
        const leaveTypeId = leave.customLeaveTypeId;
        const leaveTypeInfo = leaveTypes.find(lt => lt.id === leaveTypeId);
        
        const userName = leave.user && leave.user.firstName 
          ? `${leave.user.firstName} ${leave.user.lastName || ''}` 
          : (leave.User && leave.User.firstName 
              ? `${leave.User.firstName} ${leave.User.lastName || ''}` 
              : 'Unknown User');
              
        // Use the correct date properties and provide fallbacks
        const startDateStr = leave.start || leave.startDate;
        const endDateStr = leave.end || leave.endDate;
        
        if (!startDateStr || !endDateStr) {
          console.error(`Missing dates for event ${leave.id}:`, leave);
          return null; // Skip events with missing dates
        }
        
        // Ensure dates are properly formatted for FullCalendar
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);
        
        // For all-day events, FullCalendar expects the end date to be exclusive
        // So we need to add one day to the end date
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
        
        // Determine the leave type name to display
        let displayTypeName;
        if (leave.typeName) {
          // Use typeName if available (for custom leave types)
          displayTypeName = leave.typeName;
        } else if (leaveTypeInfo && leaveTypeInfo.name) {
          // If we found the leave type in available types, use its name
          displayTypeName = leaveTypeInfo.name;
        } else if (leave.customLeaveTypeId) {
          // If we have a customLeaveTypeId but no typeName, show a better fallback
          displayTypeName = 'Custom Leave';
        } else {
          // For standard leave types, capitalize the first letter
          displayTypeName = leave.type ? leave.type.charAt(0).toUpperCase() + leave.type.slice(1) : 'Leave';
        }
        
        // Get the color from the leave type info or from the leave itself
        const typeColor = (leaveTypeInfo && leaveTypeInfo.color) || leave.typeColor || '#6B7280';
        
        // Determine background color based on status and type
        const backgroundColor = leave.status === 'pending' ? '#FFA500' : typeColor;
        
        return {
          id: leave.id,
          title: `${userName} - ${displayTypeName}`,
          start: startDate.toISOString(),
          end: adjustedEndDate.toISOString(),
          // Force all-day event display
          allDay: true,
          backgroundColor: backgroundColor,
          borderColor: backgroundColor,
          textColor: '#ffffff',
          classNames: [`status-${leave.status}`],
          extendedProps: {
            status: leave.status,
            type: leave.type,
            typeName: displayTypeName,
            customLeaveTypeId: leave.customLeaveTypeId,
            typeColor: typeColor, // Store the determined color
            notes: leave.notes,
            user: leave.user || leave.User,
            approver: leave.approver
          }
        };
      });
      
  
      
      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
      setEvents([]);
      setBankHolidayEvents([]);
    } finally {
      setLoading(false);
    }
  };
  
  const getLeaveTypeColor = (leave) => {
    // Handle pending status with orange color
    if (leave && leave.status === 'pending') {
      return '#FFA500'; // Orange for pending
    }
    
    // If the leave object has a typeColor property, use it
    if (leave && leave.typeColor) {
      return leave.typeColor;
    }
    
    // Try to find the leave type in availableLeaveTypes
    const typeId = leave && typeof leave === 'object' ? 
      (leave.customLeaveTypeId || leave.extendedProps?.customLeaveTypeId) : leave;
    
    // Look up the color in availableLeaveTypes
    const leaveType = availableLeaveTypes.find(lt => lt.id === typeId);
    if (leaveType && leaveType.color) {
      return leaveType.color;
    }
    
    // If we have extendedProps with typeColor, use that
    if (leave && leave.extendedProps && leave.extendedProps.typeColor) {
      return leave.extendedProps.typeColor;
    }
    
    // Fallback color if type not found
    return '#6B7280'; // gray-500
  };
  
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };
  
  // Format date to display in a user-friendly format
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // Format date and time to display in a user-friendly format
  const formatDateTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
    setShowEventModal(true);
  };
  
  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };
  
  // Handle date selection for leave request
  const handleDateSelect = (selectInfo) => {
    // Get the selected dates
    const start = selectInfo.start;
    const end = selectInfo.end;
    
    console.log('Date selected:', { start, end });
    
    // Set the selected dates
    setSelectedDates({
      start,
      end: new Date(end.getTime() - 86400000) // Subtract one day as FullCalendar's end date is exclusive
    });
    
    // Show the request modal
    setShowRequestModal(true);
  };
  
  // Close the request modal
  const closeRequestModal = () => {
    setShowRequestModal(false);
    setSelectedDates({
      start: null,
      end: null
    });
    setLeaveRequest({
      type: availableLeaveTypes.length > 0 ? availableLeaveTypes[0].id : '',
      notes: ''
    });
  };
  
  // Working-day count, respecting team.workingDays (falls back to Mon-Fri).
  const calculateBusinessDays = (startDate, endDate) => {
    return countWorkingDays({ startDate, endDate, workingDays });
  };
  
  // Handle leave request form submission
  const handleLeaveRequestSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    // Calculate requested days
    const requestedDays = calculateBusinessDays(selectedDates.start, selectedDates.end);
    
    // Check if user has enough leave balance
    const balance = leaveBalances[leaveRequest.type] || {};
    const availableDays = balance.remaining || 0;
    
    // Find the leave type name for better error messages
    const selectedLeaveType = availableLeaveTypes.find(type => type.id === leaveRequest.type);
    const typeName = selectedLeaveType ? selectedLeaveType.name : leaveRequest.type;
    
    if (requestedDays > availableDays) {
      toast.error(`Insufficient ${typeName} balance. You have ${availableDays} days available but are requesting ${requestedDays} days.`);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format the request data
      const requestData = {
        startDate: selectedDates.start.toISOString(),
        endDate: selectedDates.end.toISOString(),
        leaveType: leaveRequest.type,
        reason: leaveRequest.notes
      };
      
      console.log('Submitting leave request:', requestData);
      
      // Submit the request
      const response = await axios.post('/api/leaves/request', requestData);
      
      console.log('Leave request response:', response.data);
      
      // Show success message
      toast.success('Leave request submitted successfully');
      
      // Close the modal
      closeRequestModal();
      
      // Refresh the calendar data
      fetchCalendarData();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit leave request';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle input change for leave request form
  const handleLeaveRequestChange = (e) => {
    const { name, value } = e.target;
    
    setLeaveRequest(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    
    if (name.startsWith('type-')) {
      const leaveType = name.replace('type-', '');
      setFilters(prev => ({
        ...prev,
        leaveTypes: checked 
          ? [...prev.leaveTypes, leaveType]
          : prev.leaveTypes.filter(type => type !== leaveType)
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: checked
      }));
    }
  };
  
  const filteredEvents = events.filter(event => {
    // Skip null events
    if (!event) return false;
    
    // Filter by status
    if (event.extendedProps.status === 'approved' && !filters.showApproved) return false;
    if (event.extendedProps.status === 'pending' && !filters.showPending) return false;
    if (event.extendedProps.status === 'rejected' && !filters.showRejected) return false;
    if (event.extendedProps.status === 'cancelled' && !filters.showCancelled) return false;
    
    // Filter by leave type - use a unified approach for all leave types
    if (filters.leaveTypes.length > 0) {
      // Get the type ID (either customLeaveTypeId or type)
      const typeId = event.extendedProps.customLeaveTypeId || event.extendedProps.type;
      if (!filters.leaveTypes.includes(typeId)) return false;
    }
    
    return true;
  });

  const combinedEvents = [
    ...filteredEvents,
    ...(filters.showBankHolidays ? bankHolidayEvents : [])
  ];
  
  const changeView = (viewName) => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.changeView(viewName);
  };
  
  // These functions are already defined above
  // Using the existing formatDate and formatDateTime functions
  
  // We'll continue with the render part in the next update
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent animate-fade-in">Team Calendar</h1>
          <div className="mt-3 sm:mt-0 flex space-x-2">
            <GlassButton
              onClick={() => changeView('dayGridMonth')}
              className="text-sm"
            >
              Month
            </GlassButton>
          <button
            onClick={() => changeView('timeGridWeek')}
            className="btn btn-sm btn-outline"
          >
            Week
          </button>
          <button
            onClick={() => changeView('listMonth')}
            className="btn btn-sm btn-outline"
          >
            List
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters */}
        <div className="lg:col-span-1">
          <div className="card bg-white dark:bg-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Filters</h2>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      id="show-approved"
                      name="showApproved"
                      type="checkbox"
                      checked={filters.showApproved}
                      onChange={handleFilterChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600"
                    />
                    <label htmlFor="show-approved" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                      Approved
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="show-pending"
                      name="showPending"
                      type="checkbox"
                      checked={filters.showPending}
                      onChange={handleFilterChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600"
                    />
                    <label htmlFor="show-pending" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                      Pending
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="show-bank-holidays"
                      name="showBankHolidays"
                      type="checkbox"
                      checked={filters.showBankHolidays}
                      onChange={handleFilterChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600"
                    />
                    <label htmlFor="show-bank-holidays" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                      Bank Holidays
                    </label>
                  </div>
                  {/* Only show Rejected and Cancelled filters for admins */}
                  {isAdmin && (
                    <>
                      <div className="flex items-center">
                        <input
                          id="show-rejected"
                          name="showRejected"
                          type="checkbox"
                          checked={filters.showRejected}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600"
                        />
                        <label htmlFor="show-rejected" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Rejected
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="show-cancelled"
                          name="showCancelled"
                          type="checkbox"
                          checked={filters.showCancelled}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600"
                        />
                        <label htmlFor="show-cancelled" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                          Cancelled
                        </label>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Leave Type</h3>
                <div className="space-y-2">
                  {/* All leave types - dynamically generated */}
                  {availableLeaveTypes.map(leaveType => (
                    <div key={leaveType.id} className="flex items-center">
                      <input
                        id={`type-${leaveType.id}`}
                        name={`type-${leaveType.id}`}
                        type="checkbox"
                        checked={filters.leaveTypes.includes(leaveType.id)}
                        onChange={handleFilterChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600"
                      />
                      <label htmlFor={`type-${leaveType.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                        <span 
                          className="inline-block w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: leaveType.color || '#6366F1' }}
                        ></span>
                        {leaveType.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Calendar */}
        <div className="lg:col-span-3">
          <div className="card bg-white dark:bg-gray-800 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center h-96">
                <svg className="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <div className="calendar-container p-4">

                
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,listMonth'
                  }}
                  events={combinedEvents}
                  eventClick={handleEventClick}
                  height="auto"
                  contentHeight={700}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: 'short'
                  }}
                  eventDisplay="block"
                  eventTextColor="#ffffff"
                  displayEventTime={false}
                  eventColor="#4CAF50"
                  eventBackgroundColor="#4CAF50"
                  eventBorderColor="#4CAF50"
                  selectable={true}
                  select={handleDateSelect}
                  unselectAuto={false}
                  eventDidMount={(info) => {
                    // Add status class to the event element
                    if (info.event.extendedProps && info.event.extendedProps.status) {
                      info.el.classList.add(`status-${info.event.extendedProps.status}`);
                    }
                    console.log('Event mounted:', info.event.title);
                  }}
                  datesSet={(dateInfo) => {
                    console.log('Calendar dates changed:', dateInfo);
                    // We don't need to fetch data again since we're now fetching all events
                    // This handler is here in case we want to add month-specific logic in the future
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Event Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeEventModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                        {selectedEvent.extendedProps?.isBankHoliday ? 'Bank Holiday' : 'Leave Details'}
                      </h3>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
                        onClick={closeEventModal}
                      >
                        <span className="sr-only">Close</span>
                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      {selectedEvent.extendedProps?.isBankHoliday ? (
                        <div>
                          <div className="mb-4">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Holiday</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {selectedEvent.extendedProps.bankHoliday?.title}
                            </p>
                          </div>
                          <div className="mb-4">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Date</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDate(selectedEvent.start)}
                            </p>
                          </div>
                          {selectedEvent.extendedProps.bankHoliday?.notes && (
                            <div className="mb-4">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Notes</p>
                              <p className="text-sm text-gray-900 dark:text-white">
                                {selectedEvent.extendedProps.bankHoliday.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white">
                              {selectedEvent.extendedProps.user.firstName?.charAt(0)}
                              {selectedEvent.extendedProps.user.lastName?.charAt(0)}
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {selectedEvent.title}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {selectedEvent.extendedProps.user.email}
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Leave Type</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                {selectedEvent.extendedProps.type}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                              <span className={`badge ${getStatusBadgeClass(selectedEvent.extendedProps.status)}`}>
                                {selectedEvent.extendedProps.status}
                              </span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Start Date</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatDate(selectedEvent.start)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">End Date</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatDate(selectedEvent.end)}
                              </p>
                            </div>
                          </div>
                          
                          {selectedEvent.extendedProps.notes && (
                            <div className="mb-4">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Notes</p>
                              <p className="text-sm text-gray-900 dark:text-white">
                                {selectedEvent.extendedProps.notes}
                              </p>
                            </div>
                          )}
                          
                          {selectedEvent.extendedProps.approvedBy && (
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Approved By</p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {selectedEvent.extendedProps.approvedBy.firstName} {selectedEvent.extendedProps.approvedBy.lastName}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Approved At</p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {formatDateTime(selectedEvent.extendedProps.approvedAt)}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Created At</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDateTime(selectedEvent.extendedProps.createdAt)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="btn btn-outline w-full sm:w-auto sm:ml-3"
                  onClick={closeEventModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Leave Request Modal */}
      {showRequestModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeRequestModal}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                        Request Leave
                      </h3>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
                        onClick={closeRequestModal}
                      >
                        <span className="sr-only">Close</span>
                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <form onSubmit={handleLeaveRequestSubmit}>
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Selected Dates</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedDates.start && formatDate(selectedDates.start)} to {selectedDates.end && formatDate(selectedDates.end)}
                          </p>
                        </div>
                        
                        <div className="mb-4">
                          <label htmlFor="type" className="block text-xs text-gray-500 dark:text-gray-400">
                            Leave Type
                          </label>
                          <select
                            id="type"
                            name="type"
                            value={leaveRequest.type}
                            onChange={handleLeaveRequestChange}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            required
                          >
                            {availableLeaveTypes.length === 0 ? (
                              <option value="">Loading leave types...</option>
                            ) : (
                              availableLeaveTypes.map(leaveType => (
                                <option key={leaveType.id} value={leaveType.id}>
                                  {leaveType.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        
                        <div className="mb-4">
                          <label htmlFor="notes" className="block text-xs text-gray-500 dark:text-gray-400">
                            Notes
                          </label>
                          <textarea
                            id="notes"
                            name="notes"
                            rows="3"
                            value={leaveRequest.notes}
                            onChange={handleLeaveRequestChange}
                            className="mt-1 block w-full sm:text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Add any notes about your leave request"
                          ></textarea>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn btn-primary w-full sm:w-auto sm:ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            'Submit Request'
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline w-full sm:w-auto mt-3 sm:mt-0"
                          onClick={closeRequestModal}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Calendar;
